import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components/ensigns';
import { SpecraInlineBanner } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { shortDate, timestampToDate, versionSourceWords } from './statusText';
function clock(date) {
    if (!date) return 'earlier';
    return date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit'
    });
}
/** The banners as a node, or null when there are none (hook-free; call it directly). */ export function workspaceBanners({ canComment, canEdit, notice, onDismissNotice, onReload, pending, unitNoun, userName, viewing }) {
    const banners = [];
    if (notice?.kind === 'updated') {
        const v = notice.version;
        banners.push(/*#__PURE__*/ _jsx(SpecraInlineBanner, {
            "data-testid": Testids.officeWorkspace.updatedBanner,
            onDismiss: onDismissNotice,
            tone: "info",
            children: v ? `Updated to v${v.versionNumber} (${versionSourceWords(v.source)})` : 'Updated to the latest version'
        }, "updated"));
    } else if (notice?.kind === 'newer') {
        const v = notice.version;
        const who = v?.createdByUserId ? userName(v.createdByUserId) : 'a teammate';
        banners.push(/*#__PURE__*/ _jsx(SpecraInlineBanner, {
            action: /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": Testids.officeWorkspace.reloadNow,
                onClick: onReload,
                size: "sm",
                variant: "secondary",
                children: "Reload"
            }),
            "data-testid": Testids.officeWorkspace.updatedBanner,
            tone: "warning",
            children: `A newer version was saved (${versionSourceWords(v?.source)}, ${who}). Reload to see it. Your unsaved edits since ${clock(notice.dirtySince)} will be kept as a separate version.`
        }, "newer"));
    } else if (notice?.kind === 'branch') {
        banners.push(/*#__PURE__*/ _jsx(SpecraInlineBanner, {
            "data-testid": Testids.officeWorkspace.updatedBanner,
            onDismiss: onDismissNotice,
            tone: "info",
            children: `Your edits from ${clock(notice.dirtySince)} were saved as v${notice.version.versionNumber}. Compare them in History.`
        }, "branch"));
    }
    if (pending) banners.push(/*#__PURE__*/ _jsx("div", {
        children: pending
    }, "pending"));
    if (viewing) {
        const v = viewing.version;
        const name = v ? [
            `v${v.versionNumber}`,
            v.label ? `“${v.label}”` : '',
            shortDate(timestampToDate(v.createdAt))
        ].filter(Boolean).join(' · ') : 'an older version';
        banners.push(/*#__PURE__*/ _jsx(SpecraInlineBanner, {
            action: /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap gap-[var(--space-2)]",
                children: [
                    viewing.canRestore ? /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.officeWorkspace.restoreVersion,
                        disabled: viewing.restoring,
                        isLoading: viewing.restoring,
                        onClick: viewing.onRestore,
                        size: "sm",
                        variant: "secondary",
                        children: "Restore this version"
                    }) : null,
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.officeWorkspace.backToCurrent,
                        onClick: viewing.onBack,
                        size: "sm",
                        variant: "secondary",
                        children: "Back to current"
                    })
                ]
            }),
            "data-testid": Testids.officeWorkspace.viewBanner,
            tone: "warning",
            variant: "framed",
            children: /*#__PURE__*/ _jsxs("span", {
                children: [
                    "Viewing ",
                    /*#__PURE__*/ _jsx("strong", {
                        children: name
                    }),
                    ". Read-only.",
                    viewing.error ? ` ${viewing.error}` : ''
                ]
            })
        }, "viewing"));
    }
    if (!canEdit) {
        banners.push(/*#__PURE__*/ _jsx(SpecraInlineBanner, {
            "data-testid": Testids.officeWorkspace.readOnlyBanner,
            tone: "info",
            children: `You can read this ${unitNoun}. Ask the owner for edit access.${canComment ? ' You can comment.' : ''}`
        }, "readonly"));
    }
    if (banners.length === 0) return null;
    return /*#__PURE__*/ _jsx("div", {
        className: "flex flex-col gap-[var(--space-2)]",
        children: banners
    });
}
/** Component form of `workspaceBanners`. */ export function WorkspaceBanners(props) {
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: workspaceBanners(props)
    });
}
